import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  Link,
  Modal,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  TableHead,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import EmailIcon from "@mui/icons-material/Email";

import PageTitle from "../../common/PageTitle";
import LocalDateSelector from "../../common/LocalDateSelector";
import Loader from "../../common/Loader";

import {
  getAPI,
  postAPI,
  patchAPI,
  deleteAPI,
  getExportAPI,
} from "../../network";

import { toast } from "react-toastify";
import { tableHeader, tableData } from "../../utils";
import { formatDate, formatDatePost } from "../../utils";
import { BASE_URL } from "../../../constant";

const SUPPORT_TYPES = [
  "Admin Support",
  "Invoicing Support",
  "Tech support",
  "Integration support",
  "App Support",
  "Portal Support",
  "Hardware Support",
  "User Support",
  "Other",
];

export default function SupportPage() {
  // companyId from localStorage
  const storedCompanyId = localStorage.getItem("companyIdLS");
  const companyId = storedCompanyId ? storedCompanyId : undefined;

  // States
  const [supports, setSupports] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    statusId: null,
    userId: null,
  });

  const [searchTicketNo, setSearchTicketNo] = useState("");
  const [searchSupportType, setSearchSupportType] = useState("");

  const [loader, setLoader] = useState(false);

  // Dialog / form states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [actionType, setActionType] = useState(""); // "add" or "edit"
  const [editId, setEditId] = useState(null);
  const [ticket, setTicket] = useState("");

  const [formValues, setFormValues] = useState({
    userId: "",
    title: "",
    issue: "",
    supportType: "",
    statusId: "",
    closeDate: null, // date
    closingStatement: "",
  });

  const [formErrors, setFormErrors] = useState({
    userId: false,
    title: false,
    issue: false,
    supportType: false,
    statusId: false,
    // closeDate optional in add
  });

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailReply, setEmailReply] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const rowsPerPageOptions = [10, 25, 50, 70];

  // Permissions
  const [permissionList, setPermissionList] = useState([]);
  useEffect(() => {
    try {
      const perms = JSON.parse(localStorage.getItem("permissionList")) || [];
      setPermissionList(perms);
    } catch (err) {
      console.error("Error parsing permissionList:", err);
      setPermissionList([]);
    }
  }, []);

  const submenuPermissions = permissionList?.[0]?.submenuPermissions || [];

  const canDelete = submenuPermissions.some(
    (perm) => perm.submenu === "Delete"
  );
  const canEdit = submenuPermissions.some((perm) => perm.submenu === "Edit");
  const canAdd = submenuPermissions.some((perm) => perm.submenu === "Add");
  const canMail = submenuPermissions.some((perm) => perm.submenu === "Mail");
  const showBothButtons = submenuPermissions.length === 0;

  // Debounce helpers for search inputs
  let searchTicketTimer;
  useEffect(() => {
    // trigger when searchTicketNo changes
    const handler = setTimeout(() => {
      fetchSupports();
    }, 500); // half-second debounce

    return () => clearTimeout(handler);
  }, [searchTicketNo, searchSupportType, filter, page, perPage]);

  // Initial load
  useEffect(() => {
    fetchUsers();
    fetchStatuses();
    fetchSupports();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === API calls ===

  const fetchSupports = async () => {
    setLoader(true);
    try {
      const params = new URLSearchParams();

      if (filter.startDate) {
        params.append("startDate", formatDatePost(filter.startDate));
      }
      if (filter.endDate) {
        params.append("endDate", formatDatePost(filter.endDate));
      }
      if (companyId) {
        params.append("companyId", companyId);
      }
      if (filter.userId) {
        params.append("userId", filter.userId);
      }
      if (filter.statusId) {
        params.append("statusId", filter.statusId);
      }
      if (searchTicketNo.trim() !== "") {
        params.append("ticketId", searchTicketNo.trim().toUpperCase());
      }
      if (searchSupportType.trim() !== "") {
        params.append("type", searchSupportType.trim());
      }

      const queryString = params.toString() ? `?${params.toString()}` : "";
      const supportsData = await getAPI(`/support${queryString}`);
      setSupports(Array.isArray(supportsData) ? supportsData : []);
    } catch (err) {
      console.error("Error fetching supports:", err);
      toast.error("Failed to fetch supports");
    } finally {
      setLoader(false);
    }
  };

  const fetchUsers = async () => {
    setLoader(true);
    try {
      const usersData = await getAPI(`/users/app?companyId=${companyId}`);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to fetch users");
    } finally {
      setLoader(false);
    }
  };

  const fetchStatuses = async () => {
    setLoader(true);
    try {
      const statusesData = await getAPI(
        `/support-statuses?companyId=${companyId}`
      );
      setStatuses(Array.isArray(statusesData) ? statusesData : []);
    } catch (err) {
      console.error("Error fetching statuses:", err);
      toast.error("Failed to fetch statuses");
    } finally {
      setLoader(false);
    }
  };

  // === Handlers ===

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAddDialog = () => {
    setDialogTitle("Add Support Issue");
    setActionType("add");
    setEditId(null);
    setTicket("");
    setFormValues({
      userId: "",
      title: "",
      issue: "",
      supportType: "",
      statusId: "",
      closeDate: null,
      closingStatement: "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (supportItem) => {
    setDialogTitle("Edit Support Issue");
    setActionType("edit");
    setEditId(supportItem._id);
    setTicket(supportItem.ticketId || "");
    setFormValues({
      userId: supportItem.userId?._id || "",
      title: supportItem.title || "",
      issue: supportItem.issue || "",
      supportType: supportItem.type || "",
      statusId: supportItem.statusId?._id || "",
      closeDate: supportItem.closeDate || null,
      closingStatement: supportItem.closingStatement || "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEmailDialog = (supportItem) => {
    setEditId(supportItem._id);
    setTicket(supportItem.ticketId || "");
    setEmailReply("");
    setEmailDialogOpen(true);
  };

  const openDeleteDialog = (supportItem) => {
    setEditId(supportItem._id);
    setTicket(supportItem.ticketId || "");
    setDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const closeEmailDialog = () => {
    setEmailDialogOpen(false);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Validation
  const validateForm = () => {
    const errs = {};

    if (!formValues.userId) errs.userId = true;
    if (!formValues.title) errs.title = true;
    if (!formValues.issue || formValues.issue.length < 10)
      errs.issue = true;
    if (!formValues.supportType) errs.supportType = true;
    if (!formValues.statusId) errs.statusId = true;
    // closeDate is optional for add

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    // clear the error for this field
    setFormErrors((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.warning("Please fill all required fields correctly");
      return;
    }

    const payload = {
      userId: formValues.userId,
      statusId: formValues.statusId,
      title: formValues.title,
      issue: formValues.issue,
      closingStatement: formValues.closingStatement,
      issueDate: new Date(),
      closeDate: formValues.closeDate,
      type: formValues.supportType,
    };

    setLoader(true);
    try {
      if (actionType === "add") {
        const res = await postAPI("/support", payload);
        if (res) {
          toast.success("Support created successfully");
          await fetchSupports();
          setDialogOpen(false);
        }
      } else if (actionType === "edit" && editId) {
        const res = await patchAPI(`/support/${editId}`, payload);
        if (res) {
          // toast.success("Support updated successfully");
          await fetchSupports();
          setDialogOpen(false);
        }
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error("Failed to submit");
    } finally {
      setLoader(false);
    }
  };

  const handleSubmitEmailReply = async (e) => {
    e.preventDefault();
    if (!emailReply.trim()) {
      toast.warning("Response is required!", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setLoader(true);
    try {
      const payload = {
        desc: emailReply,
        supportId: editId,
      };
      const res = await postAPI("/support/sendemail", payload);
      if (res) {
        toast.success("Email sent successfully");
        setEmailDialogOpen(false);
      }
    } catch (err) {
      console.error("Error sending email:", err);
      toast.error("Failed to send email");
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async () => {
    setLoader(true);
    try {
      const res = await deleteAPI(`/support/${editId}`);
      if (res) {
        toast.success("Deleted successfully");
        await fetchSupports();
        setDeleteDialogOpen(false);
      }
    } catch (err) {
      console.error("Error deleting support:", err);
      toast.error("Delete failed");
    } finally {
      setLoader(false);
    }
  };

  const handleExport = async () => {
    setLoader(true);
    try {
      const params = new URLSearchParams();

      if (companyId) params.append("companyId", companyId);
      if (filter.endDate) params.append("endDate", formatDatePost(filter.endDate));
      if (filter.startDate) params.append("startDate", formatDatePost(filter.startDate));
      if (filter.statusId) params.append("statusId", filter.statusId);
      if (searchTicketNo.trim() !== "") params.append("ticketId", searchTicketNo.trim().toLowerCase());
      if (searchSupportType.trim() !== "") params.append("type", searchSupportType.trim());

      const queryString = params.toString() ? `?${params.toString()}` : "";
      await getExportAPI(`${BASE_URL}/support/export${queryString}`, "Support");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export");
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box sx={{ height: "inherit" }}>
      {loader && <Loader loader={loader} />}

      <PageTitle title="Support" />

      <Grid
        container
        spacing={2}
        className="sort-box"
        sx={{ display: "flex", paddingLeft: "18px" }}
      >
        <Grid item xs={6} md={6} lg={3}>
          <FormControl fullWidth>
            <LocalDateSelector
              title="Start Date"
              value={filter.startDate}
              onChange={(value) => {
                setFilter((prev) => ({ ...prev, startDate: value }));
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6} md={6} lg={3}>
          <FormControl fullWidth>
            <LocalDateSelector
              title="End Date"
              value={filter.endDate}
              onChange={(value) => {
                setFilter((prev) => ({ ...prev, endDate: value }));
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={6} md={6} lg={2}>
          <FormControl fullWidth>
            <InputLabel>User</InputLabel>
            <Select
              label="User"
              value={filter.userId}
              onChange={(e) => {
                setFilter((prev) => ({ ...prev, userId: e.target.value }));
              }}
            >
              <MenuItem value="">
                <em>All Users</em>
              </MenuItem>
              {users.map((u) => (
                <MenuItem key={u._id} value={u._id}>
                  {u.firstname} {u.lastname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={6} lg={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filter.statusId}
              onChange={(e) =>
                setFilter((prev) => ({ ...prev, statusId: e.target.value }))
              }
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={6} lg={3}>
          <TextField
            label="Search via Ticket-Number"
            variant="outlined"
            value={searchTicketNo}
            onChange={(e) => setSearchTicketNo(e.target.value.toUpperCase())}
            fullWidth
          />
        </Grid>

        <Grid item xs={6} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Support Type</InputLabel>
            <Select
              label="Support Type"
              value={searchSupportType}
              onChange={(e) => setSearchSupportType(e.target.value)}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {SUPPORT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12} lg={12} sx={{ mt: 2, pl: 2 }}>
          <Button
            variant="contained"
            onClick={handleExport}
            startIcon={<GetAppRoundedIcon />}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ my: 4, pl: 2, pr: 2 }}>
        <TableContainer component={Paper}>
          <Table aria-label="support tickets table" className="responsive-table">
            <TableHead>
              <TableRow className="table-header" align="center">
                {[
                  "Ticket#",
                  "User",
                  "Date Logged",
                  "Date Closed",
                  "Title",
                  "Issue",
                  "Support Type",
                  "Status",
                  "Closing Statement",
                  "Action",
                ].map((head) => (
                  <TableCell key={head} align="center" sx={tableHeader}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {supports.length > 0 ? (
                supports
                  .slice(page * perPage, page * perPage + perPage)
                  .map((item) => (
                    <TableRow key={item._id}>
                      <TableCell align="center" sx={tableData}>
                        {item.ticketId?.toUpperCase() || ""}
                      </TableCell>
                      <TableCell align="center" sx={tableData}>
                        {`${item.userId?.firstname || ""} ${item.userId?.lastname || ""}`}
                      </TableCell>
                      <TableCell align="center" sx={tableData}>
                        {formatDate(item.createdAt) || ""}
                      </TableCell>
                      <TableCell align="center" sx={tableData}>
                        {item.closeDate
                          ? formatDate(item.closeDate)
                          : "NA"}
                      </TableCell>
                      <TableCell align="left" sx={tableData}>
                        {item.title || ""}
                      </TableCell>
                      <TableCell align="left" sx={tableData}>
                        {item.issue || ""}
                      </TableCell>
                      <TableCell align="center" sx={tableData}>
                        {item.type || ""}
                      </TableCell>
                      <TableCell align="center" sx={tableData}>
                        {item.statusId?.name || ""}
                      </TableCell>
                      <TableCell align="center" sx={tableData}>
                        {item.closingStatement || ""}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        {(showBothButtons || canEdit) && (
                          <Button
                            variant="outlined"
                            color="info"
                            onClick={() => openEditDialog(item)}
                          >
                            <EditIcon />
                          </Button>
                        )}
                        {(showBothButtons || canMail) && (
                          <Button
                            variant="outlined"
                            color="info"
                            component="a"
                            href={`mailto:${item.userId?.email || ""}?subject=${encodeURIComponent(
                              `Support Ticket Details#${item.ticketId?.toUpperCase() || ""}`
                            )}&body=${encodeURIComponent(
                              `Ticket#: ${item.ticketId?.toUpperCase() || ""}\nDate Logged: ${formatDate(
                                item.createdAt
                              ) || ""}\nDate Closed: ${item.closeDate
                                ? formatDate(item.closeDate)
                                : "N/A"
                              }\nIssue: ${item.title || ""}\nDescription: ${item.issue || ""
                              }\nSupport Type: ${item.type || ""}\nStatus: ${item.statusId?.name || ""
                              }`
                            )}`}
                          >
                            <EmailIcon />
                          </Button>
                        )}
                        {(showBothButtons || canDelete) && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => openDeleteDialog(item)}
                          >
                            <DeleteIcon />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    align="center"
                    sx={tableData}
                    colSpan={10}
                  >
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell colSpan={2}>
                  {(showBothButtons || canAdd) && (
                    <Link href="#" underline="none" onClick={openAddDialog}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span className="add-title">Add Support Query</span>
                        <AddCircleIcon className="add-icon" />
                      </Box>
                    </Link>
                  )}
                </TableCell>

                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  colSpan={8}
                  count={supports.length}
                  rowsPerPage={perPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth>
        <DialogTitle sx={{ mb: 4, textAlign: "center" }}>
          {dialogTitle}
        </DialogTitle>
        <Typography variant="h6" align="center">
          TICKET: {ticket?.toUpperCase()}
        </Typography>
        <DialogContent>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ "& .MuiTextField-root": { my: 2, width: "100%" } }}
          >
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={formValues.userId}
                label="User"
                onChange={(e) =>
                  handleFormChange("userId", e.target.value)
                }
              >
                {users.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    {u.firstname} {u.lastname}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.userId && (
                <Typography color="error" variant="body2">
                  User is required
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Title"
              fullWidth
              value={formValues.title}
              onChange={(e) =>
                handleFormChange("title", e.target.value)
              }
              error={!!formErrors.title}
            />

            <TextField
              label="Issue Description"
              fullWidth
              multiline
              minRows={3}
              value={formValues.issue}
              onChange={(e) =>
                handleFormChange("issue", e.target.value)
              }
              error={!!formErrors.issue}
            />

            <FormControl fullWidth>
              <InputLabel>Support Type</InputLabel>
              <Select
                value={formValues.supportType}
                label="Support Type"
                onChange={(e) =>
                  handleFormChange("supportType", e.target.value)
                }
              >
                {SUPPORT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.supportType && (
                <Typography color="error" variant="body2">
                  Support Type is required
                </Typography>
              )}
            </FormControl>

            {actionType === "edit" && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Date Closed"
                  value={formValues.closeDate}
                  onChange={(newVal) =>
                    handleFormChange("closeDate", newVal)
                  }
                  renderInput={(params) => (
                    <TextField fullWidth {...params} />
                  )}
                />
              </LocalizationProvider>
            )}

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formValues.statusId}
                label="Status"
                onChange={(e) =>
                  handleFormChange("statusId", e.target.value)
                }
              >
                {statuses.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.statusId && (
                <Typography color="error" variant="body2">
                  Status is required
                </Typography>
              )}
            </FormControl>

            {actionType === "edit" && (
              <TextField
                label="Closing Statement"
                fullWidth
                value={formValues.closingStatement}
                onChange={(e) =>
                  handleFormChange("closingStatement", e.target.value)
                }
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ mb: 2, mx: 4 }}>
          <Button variant="contained" onClick={handleSubmitForm}>
            {actionType === "add" ? "Submit" : "Edit"}
          </Button>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={closeEmailDialog} fullWidth>
        <DialogTitle sx={{ mb: 4, textAlign: "center" }}>
          Send Support Mail
        </DialogTitle>
        <Typography variant="h6" align="center">
          TICKET: {ticket.toUpperCase()}
        </Typography>
        <DialogContent>
          <TextField
            label="Response"
            fullWidth
            multiline
            minRows={3}
            value={emailReply}
            onChange={(e) => setEmailReply(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ mb: 2, mx: 4 }}>
          <Button variant="contained" onClick={handleSubmitEmailReply}>
            Send
          </Button>
          <Button variant="outlined" onClick={closeEmailDialog}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth>
        <DialogTitle sx={{ mb: 4, textAlign: "center" }}>
          Delete Support Query
        </DialogTitle>
        <DialogContent>
          <Typography align="center" fontWeight="bold">
            Do you want to delete support query ticket {ticket}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ mb: 2, mx: 4 }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button variant="outlined" onClick={closeDeleteDialog}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
